import { CommonModule, DOCUMENT } from '@angular/common'
import { Component, ChangeDetectionStrategy, Input, ViewEncapsulation, HostBinding, ChangeDetectorRef, inject, ViewChild, ElementRef, Renderer2, HostListener } from '@angular/core'
import { NgElement, WithProperties } from '@angular/elements'
import { DropdownMenuAlignment } from './dropdown-menu-alignment'
import { DropdownMenuDirection } from './dropdown-menu-direction'
import { registerCustomElementFactory } from '../../custom-elements/register-custom-element-factory'

/**
 * @public
 */
export type DropdownElement = NgElement & WithProperties<DropdownComponent>

/**
 * A component that appear as a button, and when said button is clicked a menu will appear below the button,
 * and on-top of the content below.
 *
 * @public
 */
@Component({
  selector: 'gpei-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: [ './dropdown.component.sass' ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
  standalone: true,
  imports: [ CommonModule ]
})
export class DropdownComponent {

  /**
   * Text that is displayed inside the button
   */
  @Input()
  @HostBinding()
  text: string

  /**
   * State of whether or not the menu is visible
   */
  @Input()
  @HostBinding()
  open: boolean

  @ViewChild('button')
  private readonly _buttonRef!: ElementRef<HTMLButtonElement>

  @ViewChild('menu')
  private readonly _menuRef!: ElementRef<HTMLDivElement>

  @ViewChild('slot')
  private readonly _slotRef!: ElementRef<HTMLSlotElement>

  private readonly _changeDetectorRef = inject(ChangeDetectorRef)
  private readonly _renderer2 = inject(Renderer2)
  private readonly _elementRef = inject(ElementRef)
  private readonly _document = inject(DOCUMENT)

  private _unlistenCallbacks: Array<() => void> = []
  private _menuY: number = 0
  private _menuX: number = 0
  private _menuAlign: number = DropdownMenuAlignment.END
  private _menuDirection: [ number, number ] = DropdownMenuDirection.DOWN
  private _avoidDocumentBoundary: boolean = true
  private _avoidViewportBoundary: boolean = true

  /**
   * Lifecycle hook for when the button is clicked.
   *
   * It will change the state of the menu between visible and hidden.
   */
  onButtonClick(): void {
    this.updateStaticMenuPosition()
    this.toggleMenuVisibility()
  }

  /**
   * Lifecycle hook for when the button loses focus.
   *
   * It will attempt to close the menu.
   *
   * @param event The focus event that triggered this hook.
   */
  onButtonBlur(event: FocusEvent): void {
    this.closeMenu(event)
  }

  /**
   * Lifecycle hook for when the menu loses focus.
   *
   * It will attempt to close the menu.
   *
   * @param event The focus event that triggered this hook.
   */
  onMenuBlur(event: FocusEvent): void {
    this.closeMenu(event)
  }

  /**
   * Lifecycle hook that fire when the slot content is updated
   *
   * This will remove any existing listeners and then register listeners on the slotted content so when a blur event is triggered
   * it will close the menu.
   */
  onSlottedContentChanged(): void {
    //Remove the existing listeners
    this._unlistenCallbacks.forEach(it => it())

    //Register the incoming listeners
    this._unlistenCallbacks = this._slotRef.nativeElement
      .assignedElements()
      .reduce((callbacks, it) => {
        let unlistenCallback = this._renderer2.listen(it, 'blur', event => this.closeMenu(event))
        callbacks.push(unlistenCallback)
        return callbacks
      }, [])
  }

  /**
   * Lifecycle hook for when the viewport (window) is changed.
   *
   * A change is detected when ever the viewport scrolss or is resized.
   *
   * This will update the position of the dropdown menu to match the current position of it's anchor.
   */
  @HostListener('window:scroll')
  @HostListener('window:resize')
  onViewportChanged(): void {
    if (this.open) {
      this.updateStaticMenuPosition()
    }
  }

  /**
   * Hide the menu if it is visible.
   *
   * If given an event as an argument, it will check if the cause for closing the menu is caused by clicking
   * outside of the menu.
   *
   * @param event Optional argument that provides context for the event that closes the menu.
   */
  closeMenu(event?: FocusEvent): void {
    // If the menu is being closed because of an event
    if (event) {
      let isMenu = event.relatedTarget == this._menuRef.nativeElement
      let isSelf = event.relatedTarget == this._elementRef.nativeElement
      let isSlottedMenuChild = this._slotRef.nativeElement.assignedElements().some(it => it == event.relatedTarget)

      // Break if the focus event is either the menu or an element within the menu
      if (isMenu || isSlottedMenuChild || isSelf) {
        return
      }
    }

    // Close the menu
    this.toggleMenuVisibility(false)
  }

  /**
   * Change the state of the menu between visible and hidden.
   *
   * @param value The new value to be used for the state.
   */
  toggleMenuVisibility(value = !this.open): void {
    this.open = value
    this._changeDetectorRef.detectChanges()
  }

  /**
   * Update the position of the menu.
   *
   * It will use the value of the {@link menuDirection} and {@link menuAlign} to calculate and update the position of the menu.
   */
  updateStaticMenuPosition(): void {
    // Calculate how the alignment converts into a vector
    let direction: [ number, number ] = [ ...this.menuDirection ]
    let alignment: [ number, number ] = [ 0, 0 ]

    let neutralAxis = this._menuDirection.findIndex(it => it == 0 )

    if (typeof this._menuAlign === 'number' && neutralAxis != -1) {
      alignment[neutralAxis] = this._menuAlign
    }

    // Collect any boundaries that the position must stay within
    let boundaries: DOMRect[] = []
    if (this.avoidDocumentBoundary) {
      let documentBoundary = this._document.documentElement.getBoundingClientRect()
      boundaries.push(documentBoundary)
    }

    if (this.avoidViewportBoundary) {
      let viewportBoundary = new DOMRect(
        0,
        0,
        this._document.defaultView.innerWidth,
        this._document.defaultView.innerHeight
      )
      boundaries.push(viewportBoundary)
    }

    // Update the menu position and update the display
    let buttonRect = this._buttonRef.nativeElement.getBoundingClientRect()
    let menuRect = this._menuRef.nativeElement.getBoundingClientRect()

    let [ x, y ] = this.calculateStaticPosition(
      direction,
      alignment,
      buttonRect,
      menuRect,
      boundaries
    )

    this._menuX = x
    this._menuY = y

    this._changeDetectorRef.detectChanges()
  }

  /**
   * Calculate the position of the menu based on the current position of the button, and the configured
   * alignment and direction of the displayed menu.
   *
   * @param direction The direction where the menu will pop out
   * @param alignment The alignment of the menu relative to the button
   *
   * @returns         A tuple containing the calculated X and Y position.
   */

  /**
   * Calculate the position of the menu based on the current position of the button, and the configured
   * alignment and direction of the displayed menu.
   *
   * It can be configured to avoid the given boundaries, and invert or shift the direction and alignment accordingly.
   *
   * @param direction   The direction where the menu will pop out. It is a tuple vector where the first index
   *                      of the tuple is the X axis, and the second index is the Y axis.
   * @param alignment   The alignment of the menu relative to the button. It is a tuple vector where the first index
   *                      of the tuple is the X axis, and the second index is the Y axis.
   * @param anchorRect  The rectangle for the button.
   * @param targetRect  The rectangle for the menu.
   * @param boundaries  The boundaries that the position must stay within.
   *
   * @returns           A tuple containing the calculated X and Y position.
   */
  calculateStaticPosition(
    direction: [ number, number ],
    alignment: [ number, number ],
    anchorRect: DOMRect,
    targetRect: DOMRect,
    boundaries: DOMRect[] = []
  ): [ number, number ] {
    // Calculate the boundaries
    let boundaryPositions: Array<[ { min: number, max: number }, { min: number, max: number } ]> = []
    if (boundaries.length > 0) {
      boundaryPositions = boundaries.map(it => {
        return [
          { min: it.x, max: it.right },
          { min: it.y, max: it.bottom }
        ]
      }) as Array<[ { min: number, max: number }, { min: number, max: number } ]>
    }

    // Calculate the position
    let anchorDimension: [ number, number ] = [ anchorRect.width, anchorRect.height ]
    let anchorPosition: [ number, number ] = [ anchorRect.x, anchorRect.y ]
    let targetDimension: [ number, number ] = [ targetRect.width, targetRect.height ]

    return direction.map((it, index) =>
      this.calculateStaticPositionForDirection(
        it,
        alignment[index],
        anchorPosition[index],
        anchorDimension[index],
        targetDimension[index],
        boundaryPositions.map(it => it[index])
      )
    ) as [ number, number ]
  }

  /**
   * Calculate a point along the X or Y axis for the menu to pop out at based on the position of the button.
   *
   * @param direction       The direction of the point along the current axis.
   * @param alignment       The alignment of the point relative to the button on the current axis.
   * @param anchorPosition  The point of the button on the current axis.
   * @param anchorSize      The size of the button on the current axis.
   * @param targetSize      The size of the menu on the current axis.
   * @param boundaries      The boundaries that the point must stay above or bellow for the current axis.
   *
   * @returns               A single point along an axis where the menu will open from.
   */
  calculateStaticPositionForDirection(
    direction: number,
    alignment: number,
    anchorPosition: number,
    anchorSize: number,
    targetSize: number,
    boundaries: Array<{ min: number, max: number }> = []
  ): number {
    let anchorHalfSize = anchorSize / 2
    let targetHalfSize = targetSize / 2

    let point = anchorPosition
      + anchorHalfSize
      + (anchorHalfSize * direction)
      + (targetHalfSize * direction)
      - targetHalfSize
      + (anchorHalfSize * alignment)
      - (targetHalfSize * alignment)

    // Adjust the direction and alignment if either caused the position to be out of bounds
    let directionOutOfBounds = boundaries.some(boundary =>
      (direction <= -1 && point < boundary.min)
      || (direction >= 1 && point + targetSize > boundary.max)
    )
    let adjustedDirection = directionOutOfBounds ?
      direction * -1 :
      direction

    let adjustedAlignment = alignment
    for (let boundary of boundaries) {
      if (adjustedDirection == 0 && alignment >= 0 && point < boundary.min) {
        adjustedAlignment = -1
      }
      else if (adjustedDirection == 0 && alignment <= 0 && point + targetSize > boundary.max) {
        adjustedAlignment = 1
      }
    }

    if (adjustedDirection != direction || adjustedAlignment != alignment) {
      point = this.calculateStaticPositionForDirection(
        adjustedDirection,
        adjustedAlignment,
        anchorPosition,
        anchorSize,
        targetSize
      )
    }

    return point
  }

  /**
   * The position on the X axis for the menu
   */
  get menuX(): number {
    return this._menuX
  }

  /**
   * The position on the Y axis for the menu
   */
  get menuY(): number {
    return this._menuY
  }

  /**
   * The way that the menu will align relative to the button.
   */
  @Input()
  @HostBinding()
  set menuAlign(value: string | number) {
    let pendingValue = typeof value === 'string' ?
      DropdownMenuAlignment.fromString(value) :
      value

    this._menuAlign = pendingValue
    this._changeDetectorRef.detectChanges()
  }

  get menuAlign(): number {
    return this._menuAlign
  }

  /**
   * The direction the menu will pop out at.
   */
  @Input()
  @HostBinding()
  set menuDirection(value: string | [ number, number ]) {
    let pendingValue = typeof value === 'string' ?
      DropdownMenuDirection.fromString(value) :
      value

    this._menuDirection = pendingValue
    this._changeDetectorRef.detectChanges()
  }

  get menuDirection(): [ number, number ] {
    return this._menuDirection
  }

  @Input()
  @HostBinding()
  set avoidDocumentBoundary(value: string | boolean) {
    let pendingValue = value

    // If the value is a string
    if (typeof pendingValue === 'string') {
      pendingValue = pendingValue.toLowerCase() !== 'false'
    }

    this._avoidDocumentBoundary = pendingValue
  }

  get avoidDocumentBoundary(): boolean {
    return this._avoidDocumentBoundary
  }

  @Input()
  @HostBinding()
  set avoidViewportBoundary(value: string | boolean) {
    let pendingValue = value

    // If the value is a string
    if (typeof pendingValue === 'string') {
      pendingValue = pendingValue.toLowerCase() !== 'false'
    }

    this._avoidViewportBoundary = pendingValue
  }

  get avoidViewportBoundary(): boolean {
    return this._avoidViewportBoundary
  }
}

/**
 * @public
 */
export const registerDropdownElement = registerCustomElementFactory(DropdownComponent)
